























































import { Component, Mixins, Prop } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import BrandsStore from "@/mixins/brandsStore";

// Enums
import { ProductTypes } from "@/data/enums/products";
import { DataModules } from "@/store/modules/data";
import { SortableItems } from "@/data/enums/sortableItems";

// Types
import { IProduct, IProductCategory } from "@/models/products";
import { IState } from "@/store";

@Component({
  computed: {
    ...mapState({
      rootCategories(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.categoriesModule,
          scope: this.rootCategoriesScope
        });
      },
      rootCategoriesScope(state: IState, getters) {
        return getters[`data/${this.categoriesModule}/rootCategoriesScope`](
          this.brandId,
          this.productTypes
        );
      }
    })
  }
})
export default class TreeViewSelectProducts extends Mixins(
  RequestPromises,
  BrandsStore
) {
  // Getters
  public rootCategoriesScope!: string;
  public rootCategories!: IProductCategory[];

  @Prop({ default: [ProductTypes.SINGLE_PRODUCT, ProductTypes.PRODUCT_BUNDLE] })
  public productTypes!: ProductTypes[];

  @Prop({ required: true })
  public selectedProducts!: {
    id: IProduct["id"];
    category_id: IProduct["category_id"];
  }[];

  @Prop({ default: false })
  public isDisabled!: boolean;

  @Prop({ default: 10 })
  public limit!: number;

  @Prop({ default: true })
  public disableBundles!: boolean;

  @Prop({ default: () => [] })
  public disabledProducts!: IProduct["id"][];

  @Prop({ default: true })
  public showClientLabels!: boolean;

  @Prop({ default: true })
  public isMultiselect!: boolean;

  @Prop({ default: () => ({}) })
  public readonly manualFilters!: Record<string, any>;

  // Variables
  public categoriesModule: string = DataModules.CATALOGUE_CATEGORIES;
  public sortableData = [];

  public isLoading = true;
  public isReloading = false;
  public requestError = false;

  public created() {
    if (!this.contentAvailable) {
      return;
    }

    this.isLoading = true;
    this.loadData();
  }

  public async loadData() {
    try {
      await this.getRootCategories(true);
      this.sortableData = this.mapCategories(this.rootCategories, [], 0);
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    }
    this.isLoading = false;
    this.isReloading = false;
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getRootCategories(forceReload = false) {
    return this.$store.dispatch(`data/${this.categoriesModule}/list`, {
      productTypes: this.productTypes,
      scope: this.rootCategoriesScope,
      params: {
        order: "order",
        limit: 0,
        "filter[level]": 1,
        brand_id: this.brandId
      },
      vm: this,
      ignoreStored: forceReload
    });
  }

  public get isOption() {
    return this.productTypes.includes(ProductTypes.PRODUCT_OPTION);
  }

  public get isAttribute() {
    return this.productTypes.includes(ProductTypes.PRODUCT_ATTRIBUTE);
  }

  public get i18n() {
    return {
      emptyResultsTitle: this.isOption
        ? this.$t(`_.no_product_options_attached`)
        : this.isAttribute
        ? this.$t(`_.no_product_attributes_attached`)
        : this.$t(`_.no_products_attached`),
      emptyResultsMsg: this.isOption
        ? this.$t(`_sentence.no_product_options_attached_msg`)
        : this.isAttribute
        ? this.$t(`_sentence.no_product_attributes_attached_msg`)
        : this.$t(`_sentence.no_products_attached_msg`),
      hidden: this.$t("_.hidden"),
      hiddenDescription: this.$t("_sentence.hidden_desc"),
      inactive: this.$t("_.inactive"),
      inactiveDescription: this.$t("_sentence.inactive_desc"),
      bundle: this.$t("_.bundle"),
      bundleDescription: this.$t("_sentence.product_bundle_desc")
    };
  }

  public mapCategories(
    categories: IProductCategory[],
    parentCategories,
    level
  ) {
    return this.$_.map(
      this.$_.orderBy(categories, ["order", "asc"]),
      (category: IProductCategory, index: number) => {
        return this.$_.merge(this.$_.pick(category, ["id", "name"]), {
          isOpened: false,
          isLoading: false,
          isInitialising: false,
          isFullyLoaded: false,
          offset: 0,
          type: SortableItems.CATEGORY,
          itemsTotal:
            this.$_.get(category, "products_count", 0) +
            this.$_.get(category, "sub_products_count", 0),
          itemsTotalMsg: this.itemsCounter(),
          level: level + 1,
          index,
          parentCategories,
          items: this.$_.concat(
            [],
            this.mapCategories(
              category.subcategories || [],
              this.$_.concat(parentCategories, [
                this.$_.merge(this.$_.pick(category, ["id", "name"]), {
                  level: level + 1,
                  index
                })
              ]),
              level + 1
            )
          )
        });
      }
    );
  }

  public itemsCounter() {
    return this.isOption
      ? "_plurals.product_options"
      : this.isAttribute
      ? "_plurals.product_attributes"
      : "_plurals.products";
  }

  public async loadItems(categoryId: IProductCategory["id"], offset) {
    const apiProps = {
      brand_id: this.brandId,
      "filter[product_type]": this.productTypes.join(","),
      ...this.manualFilters,
      order: "order",
      limit: this.limit,
      offset,
      with: "prices,products_options.prices,products_attributes"
    };

    apiProps[
      `filter[products${
        this.isOption ? "_options" : this.isAttribute ? "_attributes" : ""
      }_category_id]`
    ] = categoryId;

    const results = await this.$store.dispatch(
      `data/${DataModules.CATALOGUE}/list`,
      {
        productTypes: this.productTypes,
        params: apiProps,
        vm: this,
        storeData: false,
        returnData: false
      }
    );

    return {
      total: results.total,
      items: this.mapProducts(results.data)
    };
  }

  public mapProducts(products: any[]) {
    return this.$_.map(
      this.$_.orderBy(products, ["order", "asc"]),
      (product: IProductCategory) => {
        return this.$_.merge(
          product,
          {
            type: SortableItems.ITEM,
            isSelected: !!this.$_.find(this.selectedProducts, [
              "id",
              product.id
            ]),
            labels: this.$_.concat(
              this.$_.get(product, "product_type") ===
                ProductTypes.PRODUCT_BUNDLE
                ? [
                    {
                      title: this.i18n.bundle,
                      type: "is-info",
                      description: this.i18n.bundleDescription
                    }
                  ]
                : [],
              this.$_.get(product, "available_for_sales") ||
                !this.showClientLabels
                ? []
                : [
                    {
                      title: this.i18n.inactive,
                      type: "is-dark",
                      description: this.i18n.inactiveDescription
                    }
                  ],
              this.$_.get(product, "clients_can_order") ||
                !this.showClientLabels
                ? []
                : [
                    {
                      title: this.i18n.hidden,
                      type: "is-grey",
                      description: this.i18n.hiddenDescription
                    }
                  ]
            )
          },
          (this.disableBundles &&
            this.$_.get(product, "product_type") ===
              ProductTypes.PRODUCT_BUNDLE) ||
            this.disabledProducts.includes(product.id)
            ? { isDisabled: true }
            : {}
        );
      }
    );
  }

  public selectElement(item, isSelected: boolean) {
    this.$emit("selectProduct", item, isSelected);
  }

  public get contentAvailable() {
    return this.$userCan("staff_list_products") && this.isOption
      ? this.$userCan("list_product_options_categories")
      : this.isAttribute
      ? this.$userCan("list_product_attributes_categories")
      : this.$userCan("list_product_categories");
  }

  public get getGuardFuncionalities() {
    return this.isOption
      ? ["staff_list_products", "list_product_options_categories"]
      : this.isAttribute
      ? ["staff_list_products", "list_product_attributes_categories"]
      : ["staff_list_products", "list_product_categories"];
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: this.categoriesModule,
      scope: this.rootCategoriesScope,
      vm: this
    });
  }
}
